import React, { useState } from 'react';
import { Button } from '../ui/button';
import { 
  Play, 
  CheckCircle, 
  XCircle, 
  Loader2,
  Database,
  Globe,
  CreditCard,
  Zap,
  Settings,
  RefreshCw
} from 'lucide-react';
import { BACKEND_URL } from '../../config/api';

const TestCategory = ({ name, icon: Icon, results, isExpanded, onToggle }) => {
  const passed = results?.passed || 0;
  const failed = results?.failed || 0;
  const total = passed + failed;
  const allPassed = failed === 0 && passed > 0;
  
  return (
    <div className={`border rounded-lg overflow-hidden ${
      allPassed ? 'border-green-200 bg-green-50/50' : failed > 0 ? 'border-red-200 bg-red-50/50' : 'border-gray-200'
    }`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-blue-600" />
          <span className="font-medium">{name}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm">
            <span className="text-green-600">{passed}</span>
            <span className="text-gray-400">/</span>
            <span className={failed > 0 ? 'text-red-600' : 'text-gray-500'}>{total}</span>
          </span>
          {allPassed ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : failed > 0 ? (
            <XCircle className="w-5 h-5 text-red-600" />
          ) : (
            <div className="w-5 h-5 bg-gray-200 rounded-full" />
          )}
        </div>
      </button>
      
      {isExpanded && results?.results && (
        <div className="border-t p-3 space-y-2 bg-white">
          {results.results.map((test, idx) => (
            <div 
              key={idx}
              className={`flex items-center justify-between p-2 rounded text-sm ${
                test.status === 'PASS' ? 'bg-green-50' : 'bg-red-50'
              }`}
            >
              <span className="font-medium">{test.test}</span>
              <div className="flex items-center gap-2">
                {test.status === 'PASS' ? (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-600" />
                )}
                <span className={`text-xs ${test.status === 'PASS' ? 'text-green-600' : 'text-red-600'}`}>
                  {test.details || test.error || test.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const OrganizationDataTestsSection = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set(['fields']));

  const runAllTests = async () => {
    setLoading(true);
    setResults(null);
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${BACKEND_URL}/api/health/test-suites/organization-data/all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setResults(data);
        // Auto-expand failed sections
        const failedSections = new Set();
        Object.entries(data.sections || {}).forEach(([section, res]) => {
          if (res.failed > 0) failedSections.add(section);
        });
        if (failedSections.size > 0) {
          setExpandedSections(failedSections);
        }
      }
    } catch (error) {
      console.error('Error running tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const sections = [
    { key: 'fields', name: 'Field Verification', icon: Database },
    { key: 'update', name: 'Update Operations', icon: Settings }
  ];

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Database className="w-5 h-5 text-purple-600" />
              Organization Data Migration Tests
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Verifies all organization fields are migrated from MongoDB to PostgreSQL
            </p>
          </div>
          <Button 
            onClick={runAllTests} 
            disabled={loading}
            className="gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Run Tests
              </>
            )}
          </Button>
        </div>
        
        {results && (
          <div className="mt-4 flex items-center gap-4">
            <div className="text-2xl font-bold text-purple-600">
              {results.success_rate}
            </div>
            <div className="text-sm text-gray-500">
              <span className="text-green-600 font-medium">{results.total_passed} passed</span>
              {' / '}
              <span className={results.total_failed > 0 ? 'text-red-600 font-medium' : ''}>
                {results.total_failed} failed
              </span>
            </div>
          </div>
        )}
      </div>
      
      <div className="p-4 space-y-3">
        <div className="grid gap-2 text-xs text-gray-600 mb-4">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            <span>Tests: timezone, currency, country localization</span>
          </div>
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            <span>Tests: Stripe Connect & Subscription fields</span>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            <span>Tests: Actions/credits, feature flags, timestamps</span>
          </div>
        </div>
        
        {sections.map(section => (
          <TestCategory
            key={section.key}
            name={section.name}
            icon={section.icon}
            results={results?.sections?.[section.key]}
            isExpanded={expandedSections.has(section.key)}
            onToggle={() => toggleSection(section.key)}
          />
        ))}
      </div>
      
      {results && (
        <div className="p-4 border-t bg-gray-50 text-xs text-gray-500">
          Last run: {new Date(results.timestamp).toLocaleString()}
          {' • '}
          Database: {results.database_backend}
        </div>
      )}
    </div>
  );
};

export default OrganizationDataTestsSection;
